import type { League } from '@h2h/shared';
import { NationalFlagEmblem } from '../../components/ui/NationalFlagEmblems';
import { useCurrentRound, useLeagueRounds } from '../../hooks/useQueries';
import { cn } from '../../lib/cn';
import { isRoundInPlay, nextScheduledKickoff, useCountdown } from './helpers';

export function StartingSoonSection({
  joinedLeagueIds,
  cyclingLeagues,
  onOpen
}: {
  joinedLeagueIds: Set<string>;
  cyclingLeagues: League[];
  onOpen: (leagueId: string) => void;
}) {
  const joined = cyclingLeagues.filter((l) => joinedLeagueIds.has(l.leagueId));
  if (joined.length === 0) return null;

  return (
    <div className="space-y-2 px-0 pt-[14px]">
      {joined.map((league) => (
        <StartingSoonLeagueCard key={league.leagueId} league={league} onOpen={() => onOpen(league.leagueId)} />
      ))}
    </div>
  );
}

function StartingSoonLeagueCard({ league, onOpen }: { league: League; onOpen: () => void }) {
  const { data: round } = useCurrentRound(league.leagueId, league.currentRoundId);
  const { data: rounds } = useLeagueRounds(league.leagueId);
  const kickoff = nextScheduledKickoff(rounds, round);
  const parts = useCountdown(kickoff);

  if (isRoundInPlay(round?.status)) return null;
  if (kickoff == null || parts == null || parts.done) return null;

  const startDay = new Date(kickoff).toLocaleDateString('en-GB', { weekday: 'long' });
  const subtitle = `You're in · stage starts ${startDay}`;

  return (
    <StartingSoonCard
      league={league}
      title={league.name}
      subtitle={subtitle}
      days={parts.days}
      onClick={onOpen}
    />
  );
}

function StartingSoonCard({
  league,
  title,
  subtitle,
  days,
  onClick
}: {
  league: League;
  title: string;
  subtitle: string;
  days: number;
  onClick?: () => void;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'relative flex w-full items-center gap-[13px] overflow-hidden rounded-[22px] border border-black/[0.07] bg-[linear-gradient(120deg,#FFFFFF_0%,#F5FAF7_100%)] p-[14px_15px] text-left shadow-[0_14px_30px_-22px_rgba(20,20,50,0.35)]',
        onClick && 'tap'
      )}
    >
      <NationalFlagEmblem
        leagueId={league.leagueId}
        country={league.country}
        slug={league.cycling?.slug}
        size="md"
      />
      <div className="min-w-0 flex-1">
        <span className="text-[9.5px] font-extrabold uppercase tracking-[0.1em] text-accent-ink">
          Starting soon · Cycling
        </span>
        <div className="mt-[3px] text-[15px] font-extrabold tracking-tight text-ink">{title}</div>
        <div className="mt-px text-[12px] font-semibold text-ink2">{subtitle}</div>
      </div>
      <div className="flex shrink-0 flex-col items-center rounded-[14px] bg-night px-[13px] py-[9px]">
        <span className="num text-[18px] font-extrabold leading-none tabular-nums text-white">{days}</span>
        <span className="mt-0.5 text-[9px] font-bold uppercase tracking-wide text-white/60">days</span>
      </div>
    </Tag>
  );
}
